import ConsentUtil from '@utils/ConsentUtil';
import ErrorUtil from '@utils/ErrorUtil';
import PosthogUtil from '@utils/PosthogUtil';
import ThemeUtil from '@utils/ThemeUtil';
import randomstring from 'randomstring';
import Vue from 'vue';
import VueI18n from 'vue-i18n';

export default {

    setApiKey(state, apiKey) {
        state.apiKey = apiKey;
        localStorage.setItem('api_key', apiKey);
    },

    hideChecklist(state, projectId) {
        let checklists = state.checklists || [];
        if (!checklists.includes(projectId)) checklists.push(projectId);
        state.checklists = checklists;
        localStorage.setItem('checklists', JSON.stringify(checklists));
    },

    showChecklist(state, projectId) {
        let checklists = state.checklists || [];
        if (checklists.includes(projectId)) {
            checklists.splice(checklists.indexOf(projectId), 1);
        }
        state.checklists = checklists;
        localStorage.setItem('checklists', JSON.stringify(checklists));
    },

    setAlert(state, alert) {
        state.alerts.push({
            key: randomstring.generate(),
            message: alert.message,
            type: alert.type,
        });
    },

    setAlertSuccess(state, message) {
        state.alerts.push({
            key: randomstring.generate(),
            message: message,
            type: 'success',
        });
    },

    setAlertError(state, message) {
        state.alerts.push({
            key: randomstring.generate(),
            message: message,
            type: 'danger',
        });
    },

    setAlertWithError(state, error) {
        state.alerts.push({
            key: randomstring.generate(),
            message: ErrorUtil.error(error),
            type: 'danger'
        });
    },

    removeAlert(state, key) {
        let index = state.alerts.indexOf(state.alerts.find(a => a.key === key));
        state.alerts.splice(index, 1);
    },

    loading(state, loading) {
        state.loading = loading;
    },

    setModal(state, show) {
        state.modal = show;
    },

    setModalTab(state, tab) {
        state.modalTab = tab;
    },

    setDropdown(state, id) {
        state.dropdown = id;
    },

    setExpandableSection(state, title) {
        state.expandableSection = title;
    },

    setTask(state, slug) {
        state.task = slug;
    },

    setConsent(state, categories) {
        state.consent = categories;
    },

    setShowCookieBanner(state, show) {
        state.showCookieBanner = show;
    },

    setInviteToken(state, inviteToken) {
        state.inviteToken = inviteToken;
        localStorage.setItem('invite_token', inviteToken);
    },

    unsetInviteToken(state) {
        state.inviteToken = null;
        localStorage.removeItem('invite_token')
    },

    setApp(state, app) {
        state.app = app;
    },

    setUser(state, user) {
        state.user = user;
        // Set selected project.
        state.project = user.selected_project ? user.selected_project : null;
        // Set language.
        state.lang = user.lang ?? 'en';
        localStorage.setItem('lang', state.lang);
        window.i18n.locale = state.lang;
        const agency = user.agency;
        if (agency && typeof agency === 'object' && !Array.isArray(agency)) {
            state.agency = agency;
            ThemeUtil.setThemeForAgency(agency);
        }
        // Identify.
        PosthogUtil.identify(user);
        ConsentUtil.onReady('analytics', () => {
            PosthogUtil.identify(user);
        });
        ConsentUtil.onReady('marketing', () => {
            if (!window.cp) return;
            window.cp('identify', {
                user_token: user.jwt,
                user_id: String(user.id),
                billing_status: user.billing_status,
                email: user.email,
                first_name: user.first_name,
                is_sub_account: user.is_sub_account,
                last_name: user.last_name,
                signed_up_at: user.created_at,
            });
        });
    },

    setUserRelation(state, { key, value }) {
        if (state.user) {
            state.user[key] = value;
        }
    },

    setLang(state, lang) {
        state.lang = lang;
        localStorage.setItem('lang', state.lang);
        window.i18n.locale = lang;
    },

    setRef(state, ref) {
        const ref_at = Math.floor(Date.now() / 1000);

        if (!state.refFirst) {
            state.refFirst = ref;
            state.refFirstAt = ref_at;
            localStorage.setItem('ref_first', ref);
            localStorage.setItem('ref_first_at', ref_at);
        }

        state.refLast = ref;
        state.refLastAt = ref_at;
        localStorage.setItem('ref_last', ref);
        localStorage.setItem('ref_last_at', ref_at);
    },

    unsetRef(state) {
        state.refFirst = null;
        state.refFirstAt = null;
        state.refLast = null;
        state.refLastAt = null;
        localStorage.removeItem('ref_first');
        localStorage.removeItem('ref_first_at');
        localStorage.removeItem('ref_last');
        localStorage.removeItem('ref_last_at');
    },

    setTempLang(state) {
        const lang = (navigator.language || navigator.userLanguage || 'en').substring(0, 2);
        state.lang = lang;
        window.i18n.locale = lang;
    },

    setAgency(state, agency) {
        state.agency = agency;
        if (agency && typeof agency === 'object' && !Array.isArray(agency)) {
            ThemeUtil.setThemeForAgency(agency);
        }
    },

    setProject(state, project) {
        state.project = project;
    },

    setLocationId(state, locationId) {
        state.locationId = locationId;
        localStorage.setItem('location_id', state.locationId);
    },

    setProjectRelation(state, { key, value }) {
        state.project[key] = value;
    },

    toggleSidebarState(state) {
        if (state.sidebarState === 'expanded') {
            state.sidebarState = 'collapsed';
        } else {
            state.sidebarState = 'expanded';
        }
        localStorage.setItem('sidebar_state', state.sidebarState);
    },

    logout(state) {
        // Unset the theme.
        if (state.user) {
            ThemeUtil.unsetThemeForUser(state.user);
            // Clear the agency.
            const domain = window.location.hostname.toLowerCase().replace('www.', '');
            if (domain === process.env.VUE_APP_DOMAIN) {
                state.agency = null;
            }
        }
        // Clear the user.
        state.user = null;
        // Clear the api key.
        state.apiKey = null;
        localStorage.removeItem('api_key');
        PosthogUtil.reset();
        if (typeof window.Beacon === 'function') {
            window.Beacon('logout');
        }
    },

}